<template>
  <div :class="'select2-group' + (this.multiple ? ' select2-group--multiple' : '')">
    <div v-for="(values, pos) in newValue" class="select2-group__wrapper" :key="pos">
      <m4-separator
        v-if="multiple"
        :title="pos + 1"
        title-darken
        :class="pos > 0 ? 'separator--pt' : ''"
      ></m4-separator>
      <a
        v-if="multiple"
        :class="'select2-group__remove-row' + (pos > 0 ? ' select2-group__remove-row--pt' : '')"
        @click="removeGroup(pos)"
      >
        <m4-icon icon="close"></m4-icon>
      </a>
      <div
        v-for="(selectBox, index) in allSelectBoxes"
        :key="pos + '_' + index"
        :class="itemClasses(selectBox, pos, index)"
      >
        <m4-form-field
          v-if="selectBox.fieldType === 'numeric'"
          type="number"
          :placeholder="selectBox.placeholder ? selectBox.placeholder : null"
          :label-above="selectBox.labelAbove ? selectBox.labelAbove : null"
          :reactive-disabled="formFieldDisabled(selectBox, pos, index)"
          :value="formFieldValue(selectBox, pos, index)"
          @update:modelValue="(val) => onInput(pos, index, val)"
        ></m4-form-field>
        <m4-select2
          v-else
          :placeholder="selectBox.placeholder ? selectBox.placeholder : null"
          :label-above="selectBox.labelAbove ? selectBox.labelAbove : null"
          :name="selectBoxName(selectBox, pos, index)"
          :value="selectBoxValue(pos, index)"
          :empty-string="selectBox.emptyString ? selectBox.emptyString : null"
          :options-url="selectBoxUrl(selectBox, pos, index)"
          :max-results="selectBox.maxResults ? selectBox.maxResults : 0"
          :error="!!hasError(selectBox, pos, index)"
          load-on-init
          :load-on-init-with-search="selectBox.withSearch ? selectBox.withSearch : false"
          @change="(selValue, allSelected) => onChange(pos, index, selValue, allSelected)"
        ></m4-select2>
        <m4-hint v-if="errorMessage(selectBox, pos, index)" class="margin-t-sm form-field__error-hint" variant="error">
          {{ errorMessage(selectBox, pos, index) }}
        </m4-hint>
      </div>
    </div>
    <div v-if="multiple" class="select2-group__add-new">
      <m4-button tag="a" variant="primary" size="sm" left-icon="plus" @click="addNewGroup">
        {{ addNewBtnTitle }}
      </m4-button>
    </div>
  </div>
</template>

<script>
import inStyleguide from "../../../../frontend/utils/inStyleguidist";

export default {
  name: "M4Select2Group",
  data() {
    let value = this.modelValue && this.modelValue.length > 0 ? this.modelValue : this.value ? this.value : [];

    return {
      newValue: this.multiple ? (value.length > 0 ? value : [[]]) : [value],
      allSelectBoxes: this.selectBoxes
    };
  },
  props: {
    modelValue: {
      type: Array,
      default() {
        return [];
      }
    },
    value: {
      type: Array,
      default() {
        return [];
      }
    },
    multiple: {
      type: Boolean,
      default() {
        return false;
      }
    },
    addNewBtnTitle: {
      type: String,
      default() {
        return "Add New";
      }
    },
    selectBoxes: Array,
    errors: Array
  },
  emits: ["remove", "change"],
  methods: {
    addNewGroup() {
      this.newValue.push([]);
    },
    removeGroup(pos) {
      this.newValue.splice(pos, 1);
      this.$emit("remove", pos, this.newValue);
    },
    isActive(selectBox, pos, index) {
      return (
        (this.newValue[pos] && this.newValue[pos][index] !== undefined && this.newValue[pos][index].value) ||
        (this.newValue[pos] && this.newValue[pos][index - 1] !== undefined && this.newValue[pos][index - 1].value)
      );
    },
    hasError(selectBox, pos, index) {
      let errors = this.multiple ? this.errors : [this.errors];
      return errors && errors[pos] !== undefined && errors[pos][index] !== undefined && errors[pos][index];
    },
    errorMessage(selectBox, pos, index) {
      const error = this.hasError(selectBox, pos, index);
      if (typeof error === "string") {
        return error;
      }
      return null;
    },
    selectBoxName(selectBox) {
      return selectBox.name ? selectBox.name + (this.multiple ? "[]" : "") : null;
    },
    selectBoxValue(pos, index) {
      if (this.newValue && this.newValue[pos] && this.newValue[pos][index] !== undefined) {
        return this.newValue[pos][index];
      }
      return [];
    },
    selectBoxUrl(selectBox, pos, index) {
      if (index === 0 || (this.newValue && this.isActive(selectBox, pos, index) && this.allSelectBoxes[index])) {
        let optionUrl = this.allSelectBoxes[index].optionsUrl;
        let lastPart = "";
        if (index > 0) {
          let prevValue = this.selectBoxValue(pos, index - 1);
          if (prevValue) {
            lastPart = "/" + prevValue.value;
          }
        }
        if (selectBox.visible !== undefined && selectBox.visible.length > 0) {
          let condObj = null;
          selectBox.visible.map((cond) => {
            this.allSelectBoxes.map((aBox, j) => {
              if (aBox.name === cond.name && condObj === null) {
                let aBoxValue = this.selectBoxValue(pos, j);
                if (aBoxValue) {
                  if (cond.values.indexOf(aBoxValue.value + "") >= 0) {
                    condObj = {
                      index: j,
                      obj: aBox
                    };
                  }
                }
              }
            });

            if (condObj !== null) {
              let condSelectBoxValue = this.selectBoxValue(pos, condObj.index);
              let getVarStart = "?";
              if (optionUrl.indexOf("?") >= 0) {
                getVarStart = "&";
              }
              lastPart += getVarStart + condObj.obj.name + "=" + condSelectBoxValue.value;
            }
          });

          if (condObj === null) {
            return null;
          }
        }
        return inStyleguide() ? optionUrl : optionUrl + lastPart;
      }

      return null;
    },
    formFieldValue(formField, pos, index) {
      if (this.newValue && this.newValue[pos] && this.newValue[pos][index] !== undefined) {
        return this.newValue[pos][index].value;
      }
      return formField.defaultValue !== undefined ? formField.defaultValue : null;
    },
    formFieldDisabled(formField, pos, index) {
      if (index === 0 || (this.newValue && this.isActive(formField, pos, index) && this.allSelectBoxes[index])) {
        if (formField.visible !== undefined && formField.visible.length > 0) {
          let condObj = null;
          formField.visible.map((cond) => {
            this.allSelectBoxes.map((aBox, j) => {
              if (aBox.name === cond.name && condObj === null) {
                let aBoxValue = this.selectBoxValue(pos, j);
                if (aBoxValue) {
                  if (cond.values.indexOf(aBoxValue.value + "") >= 0) {
                    condObj = {
                      index: j,
                      obj: aBox
                    };
                  }
                }
              }
            });
          });

          return condObj === null;
        } else {
          return false;
        }
      }

      return true;
    },
    itemClasses(selectBox, pos, index) {
      let classes = ["select2-group__item"];
      if (index === 0 || this.isActive(selectBox, pos, index)) {
        let selectBoxUlr =
          selectBox.fieldType === undefined || selectBox.fieldType === "select"
            ? this.selectBoxUrl(selectBox, pos, index)
            : true;

        if (selectBox.disabled) {
          classes.push("select2-group__item--disabled");
        } else if (selectBoxUlr) {
          classes.push("select2-group__item--active");
        }
      }
      return classes;
    },
    onChange(pos, index, selValue, allSelected) {
      let newValue = [];
      // add new value
      if (allSelected.length) {
        this.allSelectBoxes.map((selectBox, j) => {
          if (index === j) {
            newValue.push(selValue);
          } else if (this.newValue && this.newValue[pos] && this.newValue[pos][j] !== undefined && index > j) {
            newValue.push(this.newValue[pos][j]);
          }
        });
        // prefill another field
        if (selValue.prefillNext) {
          newValue.push(selValue.prefillNext);
        }
        // remove value
      } else {
        this.allSelectBoxes.map((selectBox, j) => {
          if (this.newValue && this.newValue[pos] && this.newValue[pos][j] !== undefined && index > j) {
            newValue.push(this.newValue[pos][j]);
          }
        });
      }
      this.newValue[pos] = newValue;
      this.$forceUpdate();
      this.$emit("change", pos, index, selValue, allSelected, newValue, this.newValue);
    },
    onInput(pos, index, value) {
      const valObj = {
        label: value,
        value: value
      };
      this.newValue[pos][index] = valObj;
      this.$forceUpdate();
      this.$emit("change", pos, index, valObj, [valObj], valObj, this.newValue);
    }
  },
  watch: {
    value(value) {
      this.newValue = this.multiple ? (value.length > 0 ? value : [[]]) : [value];
    },
    modelValue(value) {
      this.newValue = this.multiple ? (value.length > 0 ? value : [[]]) : [value];
    }
  }
};
</script>
